import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { listBiometricDeviceRecords } from "@/lib/admin/biometricDevicesStore";
import { listCompanyRecords } from "@/lib/admin/companiesStore";
import type { CompanyBiometricDeviceFormState } from "@/lib/admin/companyBiometricDeviceForm";
import { formatBiometricDeviceProtocol } from "@/lib/admin/biometricDeviceForm";
import { listWarehouseRecords } from "@/lib/catalog/warehousesStore";
import { useMemo } from "react";

type Props = {
  form: CompanyBiometricDeviceFormState;
  onChange: (patch: Partial<CompanyBiometricDeviceFormState>) => void;
  showCompanyPicker: boolean;
};

export function CompanyBiometricDeviceUpsertFormFields({ form, onChange, showCompanyPicker }: Props) {
  const companies = useMemo(() => listCompanyRecords().slice().sort((a, b) => a.name.localeCompare(b.name)), []);
  const biometricDevices = useMemo(
    () => listBiometricDeviceRecords().slice().sort((a, b) => a.model.localeCompare(b.model)),
    [],
  );
  const warehouses = useMemo(
    () => listWarehouseRecords().filter((w) => w.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {showCompanyPicker ? (
        <div className="space-y-2 sm:col-span-3">
          <Label className="text-xs text-muted-foreground">Company *</Label>
          <Select value={form.companyId || undefined} onValueChange={(v) => onChange({ companyId: v })}>
            <SelectTrigger className="glass-input h-10">
              <SelectValue placeholder="Select company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div className="space-y-2 sm:col-span-3">
        <Label className="text-xs text-muted-foreground">Device type (catalog) *</Label>
        <Select value={form.biometricDeviceId || undefined} onValueChange={(v) => onChange({ biometricDeviceId: v })}>
          <SelectTrigger className="glass-input h-10">
            <SelectValue placeholder="Select biometric device model" />
          </SelectTrigger>
          <SelectContent>
            {biometricDevices.length === 0 ? (
              <SelectItem value="__none__" disabled>
                No catalog devices — add one under Biometric devices first
              </SelectItem>
            ) : (
              biometricDevices.map((d) => (
                <SelectItem key={d.id} value={String(d.id)}>
                  {d.model}
                  {d.driver ? ` · ${d.driver}` : ""}
                  {d.protocol ? ` · ${formatBiometricDeviceProtocol(d.protocol)}` : ""}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Warehouse</Label>
        <Select
          value={form.warehouseId || "__none__"}
          onValueChange={(v) => onChange({ warehouseId: v === "__none__" ? "" : v })}
        >
          <SelectTrigger className="glass-input h-10">
            <SelectValue placeholder="Select warehouse (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No warehouse</SelectItem>
            {warehouses.length === 0 ? (
              <SelectItem value="__empty__" disabled>
                No active warehouses — add one under Warehouses first
              </SelectItem>
            ) : (
              warehouses.map((w) => (
                <SelectItem key={w.id} value={String(w.id)}>
                  {w.name}
                  {w.city?.trim() ? ` · ${w.city.trim()}` : ""}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Communication password</Label>
        <Input
          className="glass-input h-10"
          type="password"
          value={form.communicationPassword}
          onChange={(e) => onChange({ communicationPassword: e.target.value })}
          placeholder="Optional device comm key"
          autoComplete="new-password"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Location</Label>
        <Input
          className="glass-input h-10"
          value={form.location}
          onChange={(e) => onChange({ location: e.target.value })}
          placeholder="e.g. Reception entrance"
          autoComplete="off"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">IP address *</Label>
        <Input
          className="glass-input h-10"
          value={form.ipAddress}
          onChange={(e) => onChange({ ipAddress: e.target.value })}
          placeholder="192.168.1.100"
          autoComplete="off"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Port</Label>
        <Input
          className="glass-input h-10"
          type="number"
          min={1}
          max={65535}
          value={form.port}
          onChange={(e) => onChange({ port: e.target.value })}
          placeholder="4370"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Serial number</Label>
        <Input
          className="glass-input h-10"
          value={form.serialNumber}
          onChange={(e) => onChange({ serialNumber: e.target.value })}
          placeholder="Optional"
          autoComplete="off"
        />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-3 sm:col-span-3">
        <div className="space-y-0.5">
          <Label className="text-sm">Active</Label>
          <p className="text-xs text-muted-foreground">Inactive devices are ignored for sync.</p>
        </div>
        <Switch checked={form.isActive} onCheckedChange={(v) => onChange({ isActive: Boolean(v) })} />
      </div>
    </div>
  );
}
